import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  CheckCircle2, AlertCircle, AlertTriangle, Loader2, Activity, Clock,
} from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_VARIANTS = {
  operational: {
    icon: CheckCircle2, color: 'text-primary', bg: 'bg-primary/10', border: 'border-primary/40', label: 'Operational',
  },
  degraded: {
    icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/40', label: 'Degraded',
  },
  outage: {
    icon: AlertCircle, color: 'text-red-500', bg: 'bg-red-500/10', border: 'border-red-500/50', label: 'Outage',
  },
  unknown: {
    icon: Activity, color: 'text-muted-foreground', bg: 'bg-muted/20', border: 'border-border', label: 'Unknown',
  },
};

const formatUptime = (s) => {
  if (s == null) return '—';
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

/**
 * Public status page rendered at /status. No auth required. Polls every
 * 30 seconds so customers can leave the tab open during an incident and
 * see live recovery.
 */
const SystemStatus = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/public/status`);
        if (!cancelled) {
          setData(r.data);
          setError(null);
          setLastUpdated(new Date());
        }
      } catch (err) {
        if (!cancelled) {
          setError('Unable to reach the status service');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchStatus();
    const id = setInterval(fetchStatus, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-primary" />
      </div>
    );
  }

  // Even when the API is unreachable, we still render a sensible fallback so
  // visitors don't see a blank screen during a real outage.
  const overall = data?.overall_status || (error ? 'outage' : 'unknown');
  const variant = STATUS_VARIANTS[overall] || STATUS_VARIANTS.unknown;
  const HeadIcon = variant.icon;

  return (
    <div className="min-h-screen bg-background" data-testid="system-status-page">
      <header className="border-b border-border bg-card/50 backdrop-blur">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <div>
            <div className="text-2xl font-black">
              NEXT<span className="text-primary">/</span>GEN
            </div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mt-1">
              System Status
            </div>
          </div>
          <a href="/" className="text-xs text-primary hover:underline">Back to site</a>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 space-y-6">
        {/* Overall banner */}
        <Card className={`p-6 ${variant.bg} ${variant.border} border-2`} data-testid="status-overall-card">
          <div className="flex items-center gap-4">
            <HeadIcon className={`w-10 h-10 ${variant.color}`} />
            <div>
              <h1 className={`text-2xl sm:text-3xl font-black ${variant.color}`}>
                {overall === 'operational' && 'All systems operational'}
                {overall === 'degraded' && 'Some systems are degraded'}
                {overall === 'outage' && 'We are experiencing an outage'}
                {overall === 'unknown' && 'Status unknown'}
              </h1>
              <p className="text-xs text-muted-foreground mt-1">
                {data?.checked_at && `Last checked ${format(new Date(data.checked_at), 'h:mm:ss a · MMM dd, yyyy')}`}
                {data?.uptime_seconds != null && ` · API uptime ${formatUptime(data.uptime_seconds)}`}
              </p>
            </div>
          </div>
        </Card>

        {/* Component health */}
        <Card className="p-6 border-border">
          <h2 className="text-sm uppercase tracking-widest text-muted-foreground mb-4">Components</h2>
          <div className="space-y-3">
            {(data?.components || []).map((c) => {
              const v = STATUS_VARIANTS[c.status] || STATUS_VARIANTS.unknown;
              const Icon = v.icon;
              return (
                <div
                  key={c.name}
                  className="flex items-center justify-between p-3 rounded-md border border-border bg-muted/10"
                  data-testid={`status-component-${c.name}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Icon className={`w-5 h-5 shrink-0 ${v.color}`} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{c.name}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{c.message}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {c.latency_ms != null && (
                      <span className="text-[10px] font-mono text-muted-foreground">{c.latency_ms}ms</span>
                    )}
                    <Badge variant="outline" className={`text-[10px] uppercase tracking-widest ${v.color} ${v.border}`}>
                      {v.label}
                    </Badge>
                  </div>
                </div>
              );
            })}
            {error && (
              <div className="p-3 rounded-md border border-red-500/40 bg-red-500/5 text-sm text-red-500" data-testid="status-fetch-error">
                {error}
              </div>
            )}
          </div>
        </Card>

        {/* Incident history */}
        <Card className="p-6 border-border">
          <h2 className="text-sm uppercase tracking-widest text-muted-foreground mb-4 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Recent Incidents
          </h2>
          {!data?.incidents || data.incidents.length === 0 ? (
            <p className="text-sm text-muted-foreground py-3 text-center">
              No incidents reported. We'll post any issues here as they happen.
            </p>
          ) : (
            <div className="space-y-3">
              {data.incidents.map((inc, i) => (
                <div key={inc.incident_id || i} className="p-3 rounded-md border border-border" data-testid={`status-incident-${i}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="text-[10px] uppercase">{inc.severity || 'minor'}</Badge>
                    <span className="text-xs font-medium">{inc.title}</span>
                  </div>
                  {inc.description && <p className="text-xs text-muted-foreground">{inc.description}</p>}
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {inc.created_at && format(new Date(inc.created_at), 'MMM dd, yyyy · h:mm a')}
                  </p>
                </div>
              ))}
            </div>
          )}
        </Card>

        <footer className="text-center text-xs text-muted-foreground pt-6 space-y-1">
          <p>Need help right now? Call <a href="tel:7403025607" className="text-primary">740-302-5607</a></p>
          <p>This page auto-refreshes every 30 seconds.</p>
          <p>&copy; {new Date().getFullYear()} {data?.company || 'NextGen Network Solutions'}</p>
        </footer>
      </main>
    </div>
  );
};

export default SystemStatus;
